import { forwardRef, useRef } from 'react';

import Times from '@decisiv/iconix/lib/components/Times';
import ChevronUp from '@decisiv/iconix/lib/components/ChevronUp';
import ChevronDown from '@decisiv/iconix/lib/components/ChevronDown';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import { DropdownListProps } from '@decisiv/ui-components/lib/components/DropdownList';
import Button from '@decisiv/ui-components/lib/components/Button';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import Tag from '@decisiv/ui-components/lib/components/Tag';

import { DropdownListItem } from '@decisiv/ui-components/lib/components/DropdownList/types';
import { BorderlessInput, TagContainer, TagInputContainer } from './styles';

type TargetProps = {
  label: string;
  name: string;
  disabled: boolean;
  selectedIds: string[];
  items: DropdownListProps['items'];
  isPopoverVisible: boolean;
  togglePopover: () => void;
  removeTag: (id: string | undefined) => void;
  searchText: string;
  onSearchTextChange: (text: string) => void;
};

const Target = forwardRef<HTMLDivElement, TargetProps>(
  (
    {
      name,
      label,
      items,
      disabled,
      selectedIds,
      removeTag,
      togglePopover,
      isPopoverVisible,
      searchText,
      onSearchTextChange,
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    /* istanbul ignore next */
    const icon = isPopoverVisible ? ChevronUp : ChevronDown;

    const tagItems = selectedIds
      ?.map((id) => {
        const foundOption = items?.find((option) => option.id === id);
        if (foundOption) {
          return foundOption;
        }
        return null;
      })
      .filter(Boolean) as DropdownListItem[];

    const onTagClicked = (e: React.MouseEvent<HTMLDivElement>) => {
      /* istanbul ignore next -- @preserve */
      if (disabled) return;
      const target = e.target as HTMLElement;
      const tagged = target?.closest('[data-tag="selected-tag"]');
      if (tagged) {
        return;
      }
      if (target === inputRef.current && isPopoverVisible) {
        return;
      }
      togglePopover();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      /* istanbul ignore next -- @preserve */
      if (disabled) return;
      onSearchTextChange?.(e.target.value);
      if (!isPopoverVisible) {
        togglePopover();
      }
    };
    const containerId = `${name}-tag-container`;

    return (
      <TagContainer
        ref={ref}
        onClick={onTagClicked}
        disabled={disabled}
        aria-disabled={disabled}
      >
        <P as="label" size="small" shade={1} htmlFor={containerId}>
          {label}
        </P>
        <Flex flex={1} marginTop={0.5}>
          <TagInputContainer>
            {tagItems.map((item) => (
              <Tag
                data-tag="selected-tag"
                key={item?.id}
                text={item?.label}
                actionIcon={disabled ? undefined : Times}
                action={disabled ? undefined : () => removeTag(item.id)}
                actionAttributes={{
                  'aria-label': `Remove ${item?.label} filter`,
                }}
              />
            ))}
            <BorderlessInput
              id={containerId}
              type="text"
              ref={inputRef}
              name={name}
              value={searchText}
              disabled={disabled}
              onChange={handleInputChange}
            />
          </TagInputContainer>
          <Flex justifySelf="end" alignSelf="end" marginRight={1}>
            <Button
              tabIndex={-1}
              icon={icon}
              variant="ghost"
              size="small"
              disabled={disabled}
            />
          </Flex>
        </Flex>
      </TagContainer>
    );
  },
);

export default Target;
